import type { ComparisonItem } from '../components/Topbar';
import { COMPARE_COLORS, MAX_COMPARE_ITEMS } from '../components/Topbar';
import {
  DEFAULT_GROWTH_SUBVIEW,
  growthDiveSection,
  resolveGrowthDive,
  resolveGrowthFacet,
  resolveGrowthSubview,
  retiredGrowthDiveSection,
  TRACIFY_DEMO_LINK,
  type GrowthDive,
  type GrowthFacet,
  type GrowthSubview,
} from '../data/growthTypes';
import { GROWTH_DEMO_LINK } from '../data/growthDemo';
import { SOURCE_ORDER } from '../data/reservationSources';
import { scopeFilterLabel, scopeGroupIdFromFilter } from './scopeFilter';
import {
  AGGREGATIONS,
  CAPACITY_BASES,
  COMPARE_KINDS,
  coercePerfSettings,
  DEFAULT_CAPACITY_BASIS,
  DEFAULT_PERF_SETTINGS,
  effectiveCompareKind,
  FRAME_UNITS,
} from './perfSettings';
import type {
  FleetRestaurant,
  PerfSettings,
  ReservationSourceId,
  ReservationStatusGroup,
  ScopeGroupSummary,
} from '../types';
import { RESERVATION_STATUS_GROUPS } from '../types';

export type OpsUrlMode =
  | 'live'
  | 'performance'
  | 'settings'
  | 'growth'
  | 'horizon'
  | 'hotel'
  | 'assistant';

export type OpsUrlState = {
  mode: OpsUrlMode;
  regionFilter: string | null;
  /**
   * The venue ids a `group:` or `org` filter showed (`sv`): a shared link
   * carries the venues, never a private group's definition, so its
   * recipient sees those venues as far as they can open them (decision 7.7.2).
   */
  scopeVenueIds?: string[];
  compareFilters: string[];
  source: ReservationSourceId[];
  status: ReservationStatusGroup[];
  shifts?: string[];
  perfSettings: PerfSettings;
  drillKey: string | null;
  perfDrill: { metricKey: string; outletId: string | null } | null;
  focusOutletId: string | null;
  northStarDrill: boolean;
  growthView?: GrowthSubview;
  /** Open Funnel card dive (`gd`), so a shared link reopens the same pane. */
  growthDive?: GrowthDive | null;
  /** Active dive facet (`gf`) — pages, queries, campaigns, terms. */
  growthFacet?: GrowthFacet | null;
  /** Channel whose drill is open in Growth, so a shared link reopens it. */
  growthChannel?: string | null;
  /** Attribution model shown in Growth (`gm`), so a view or demo can be shared (WP4.2). */
  growthModel?: string | null;
  /** Growth shows the demo group (`demo=group`): fictional data, never mixed with real venues. */
  growthDemo?: boolean;
  horizonDate?: string | null;
  horizonOutlet?: string | null;
  /** Outlook window on the Forecast tab (`hw`), e.g. `next30`. */
  horizonWindow?: string | null;
  /** Hotel tab (`hv=management`); the Service tab is the default. */
  hotelView?: HotelTab;
  assistantPane?: 'notebook' | 'dashboard';
  /**
   * `vc=1`: the URL describes the whole view context, so a part it leaves
   * out is that part's default (lib/viewContext.ts). The app writes it into
   * every URL; links without it name only the parts they carry.
   */
  viewContextMarked?: boolean;
};

export const SHARE_PARAM_KEYS = new Set([
  'm',
  's',
  'sv',
  'c',
  'src',
  'st',
  'agg',
  'fc',
  'fu',
  'ds',
  'de',
  'cb',
  'cm',
  'cds',
  'cde',
  'vc',
  'drill',
  'pd',
  'focus',
  'ns',
  'gv',
  'gd',
  'gf',
  'gc',
  'gp',
  'gm',
  'hd',
  'ho',
  'hw',
  'hv',
  'ap',
  'sh',
  'demo',
]);

/** Filterable booking origins only — exclude POS-only `unattributed`. */
const SOURCE_IDS = new Set<ReservationSourceId>(
  SOURCE_ORDER.filter(id => id !== 'unattributed'),
);
const STATUS_IDS = new Set<ReservationStatusGroup>(
  RESERVATION_STATUS_GROUPS.map(g => g.id),
);

export const DEFAULT_OPS_URL_STATE: OpsUrlState = {
  mode: 'live',
  regionFilter: 'all',
  compareFilters: [],
  source: [],
  status: [],
  perfSettings: DEFAULT_PERF_SETTINGS,
  drillKey: null,
  perfDrill: null,
  focusOutletId: null,
  northStarDrill: false,
};

export function hasShareParams(search: string): boolean {
  const params = new URLSearchParams(search);
  for (const key of params.keys()) {
    if (SHARE_PARAM_KEYS.has(key)) return true;
  }
  return false;
}

function parseMode(raw: string | null): OpsUrlMode {
  if (raw === 'perf') return 'performance';
  if (raw === 'set') return 'settings';
  if (raw === 'growth') return 'growth';
  if (raw === 'horizon') return 'horizon';
  if (raw === 'hotel') return 'hotel';
  if (raw === 'ai') return 'assistant';
  return 'live';
}

function encodeMode(mode: OpsUrlMode): string | null {
  if (mode === 'performance') return 'perf';
  if (mode === 'settings') return 'set';
  if (mode === 'growth') return 'growth';
  if (mode === 'horizon') return 'horizon';
  if (mode === 'hotel') return 'hotel';
  if (mode === 'assistant') return 'ai';
  return null;
}

/** The Hotel section's two tabs: the floor team's and the general manager's. */
export type HotelTab = 'service' | 'management';

function parseHotelTab(raw: string | null): HotelTab {
  return raw === 'management' ? 'management' : 'service';
}

function parseGrowthView(raw: string | null): GrowthSubview {
  return resolveGrowthSubview(raw);
}

/** Keep old shared links (`guests`) compatible with the canonical UI key (`covers`). */
function decodeMetricKey(raw: string): string {
  return raw === 'guests' ? 'covers' : raw;
}

function encodeMetricKey(raw: string): string {
  return raw === 'covers' ? 'guests' : raw;
}

function parseSource(values: string[]): ReservationSourceId[] {
  const out: ReservationSourceId[] = [];
  for (const v of values) {
    if (SOURCE_IDS.has(v as ReservationSourceId)) {
      out.push(v as ReservationSourceId);
    }
  }
  return out;
}

function parseStatus(values: string[]): ReservationStatusGroup[] {
  const out: ReservationStatusGroup[] = [];
  for (const v of values) {
    if (STATUS_IDS.has(v as ReservationStatusGroup)) {
      out.push(v as ReservationStatusGroup);
    }
  }
  return out;
}

function parseShifts(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const label = v.trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

function parsePerfSettings(
  agg: string | null,
  fc: string | null,
  fu: string | null,
  ds: string | null,
  de: string | null,
  cb: string | null,
  cm: string | null,
  cds: string | null,
  cde: string | null,
): PerfSettings {
  const partial: Partial<PerfSettings> = {};
  if (agg && AGGREGATIONS.includes(agg as PerfSettings['aggregation'])) {
    partial.aggregation = agg as PerfSettings['aggregation'];
  }
  if (fu && FRAME_UNITS.includes(fu as PerfSettings['frameUnit'])) {
    partial.frameUnit = fu as PerfSettings['frameUnit'];
  }
  if (fc != null) {
    const n = Number(fc);
    if (Number.isFinite(n) && n >= 1) {
      partial.frameCount = Math.round(n);
    }
  }
  if (ds) partial.dateStart = ds;
  if (de) partial.dateEnd = de;
  if (cb && CAPACITY_BASES.includes(cb as PerfSettings['capacityBasis'] & string)) {
    partial.capacityBasis = cb as PerfSettings['capacityBasis'];
  }
  if (cm && COMPARE_KINDS.includes(cm as PerfSettings['compareKind'] & string)) {
    partial.compareKind = cm as PerfSettings['compareKind'];
    // Only a chosen comparison is in the URL (WP3.1).
    partial.compareExplicit = true;
  }
  if (cds) partial.compareDateStart = cds;
  if (cde) partial.compareDateEnd = cde;
  return coercePerfSettings({ ...DEFAULT_PERF_SETTINGS, ...partial });
}

function perfSettingsDiffer(a: PerfSettings, b: PerfSettings): boolean {
  return (
    a.aggregation !== b.aggregation ||
    a.frameCount !== b.frameCount ||
    a.frameUnit !== b.frameUnit ||
    (a.dateStart ?? null) !== (b.dateStart ?? null) ||
    (a.dateEnd ?? null) !== (b.dateEnd ?? null) ||
    (a.capacityBasis ?? DEFAULT_CAPACITY_BASIS) !==
      (b.capacityBasis ?? DEFAULT_CAPACITY_BASIS) ||
    effectiveCompareKind(a) !== effectiveCompareKind(b) ||
    Boolean(a.compareExplicit) !== Boolean(b.compareExplicit) ||
    (a.compareDateStart ?? null) !== (b.compareDateStart ?? null) ||
    (a.compareDateEnd ?? null) !== (b.compareDateEnd ?? null)
  );
}

/**
 * The venue filter an old Growth link's `gp` meant (decision 7.7.1): a venue
 * is that outlet, a brand its scope group, the fleet or a Google Analytics
 * property "All venues of" the customer's group (a property's venues are
 * only known once Growth loads, and they lie inside that group).
 */
export function legacyGrowthVenueFilter(gp: string | null | undefined): string | null {
  const key = (gp || '').trim();
  if (!key) return null;
  if (key.startsWith('brand:')) {
    const id = key.slice('brand:'.length);
    return id ? `group:${id}` : null;
  }
  // A venue id is letters and digits; anything else was the fleet or a property.
  if (key !== 'fleet' && /^[A-Za-z0-9]+$/.test(key)) return `outlet:${key}`;
  return 'org';
}

export function decodeOpsUrlState(search: string): OpsUrlState {
  const params = new URLSearchParams(search);
  const compareRaw = params.getAll('c').slice(0, MAX_COMPARE_ITEMS);
  const compareFilters = compareRaw.filter(Boolean);
  const isComparing = compareFilters.length >= 2;

  let regionFilter = DEFAULT_OPS_URL_STATE.regionFilter;
  if (!isComparing) {
    const scope = params.get('s');
    if (scope) regionFilter = scope;
  }
  const scopeVenueIds = [
    ...new Set(
      (params.get('sv') || '')
        .split(',')
        .map(id => id.trim())
        .filter(Boolean),
    ),
  ];

  const pdRaw = params.get('pd');
  let perfDrill: OpsUrlState['perfDrill'] = null;
  if (pdRaw) {
    const [metricKey, outletId] = pdRaw.split('~', 2);
    if (metricKey) {
      perfDrill = {
        metricKey: decodeMetricKey(metricKey),
        outletId: outletId || null,
      };
    }
  }

  // A5: `?demo=tracify` is a shareable way into the Tracify demo.
  const demoLink = params.get('demo') === TRACIFY_DEMO_LINK;
  // `?demo=group` opens Growth on the demo group (Linden Group, fictional).
  const groupDemo = params.get('demo') === GROWTH_DEMO_LINK;
  const mode = (demoLink || groupDemo) && !params.get('m') ? 'growth' : parseMode(params.get('m'));
  // Growth's own picker is gone (decision 7.7.1): an old link's `gp` names
  // the same venues in the context, unless the link also names `s`.
  if (mode === 'growth' && !isComparing && !params.get('s') && !groupDemo) {
    const legacy = legacyGrowthVenueFilter(params.get('gp'));
    if (legacy) regionFilter = legacy;
  }
  const rawGrowthView = params.get('gv') ?? (demoLink ? 'sources' : null);
  // WP4.6: a dive only a typed URL reached opens the section that replaced it.
  const retiredDive = mode === 'growth' ? retiredGrowthDiveSection(params.get('gd')) : null;
  const explicitDive = mode === 'growth' && !retiredDive ? resolveGrowthDive(params.get('gd')) : null;
  // WP5.1: a Google product's dive opens in Local and search, the website's
  // in Website and reservation, whichever of the two a link names.
  const parsedView = mode === 'growth' ? retiredDive ?? parseGrowthView(rawGrowthView) : undefined;
  const diveHome = growthDiveSection(explicitDive);
  const growthView =
    diveHome && (parsedView === 'website' || parsedView === 'local') ? diveHome : parsedView;
  const legacyChannelDive =
    mode === 'growth' &&
    !explicitDive &&
    (rawGrowthView === 'paid' || rawGrowthView === 'organic')
      ? 'site'
      : null;
  const growthDive = explicitDive || legacyChannelDive;
  const growthFacet =
    mode === 'growth' && growthDive ? resolveGrowthFacet(params.get('gf')) : null;
  const analyticsOpen = growthView === 'website' && growthDive === 'site';
  const shifts = parseShifts(params.getAll('sh'));

  return {
    mode,
    regionFilter,
    ...(!isComparing && scopeVenueIds.length ? { scopeVenueIds } : {}),
    compareFilters: isComparing ? compareFilters : [],
    source: parseSource(params.getAll('src')),
    status: parseStatus(params.getAll('st')),
    ...(shifts.length ? { shifts } : {}),
    perfSettings: parsePerfSettings(
      params.get('agg'),
      params.get('fc'),
      params.get('fu'),
      params.get('ds'),
      params.get('de'),
      params.get('cb'),
      params.get('cm'),
      params.get('cds'),
      params.get('cde'),
    ),
    drillKey: params.get('drill') || null,
    perfDrill,
    focusOutletId: params.get('focus') || null,
    northStarDrill: params.get('ns') === '1',
    // A growth subview only means anything in growth mode; other modes leave
    // it unset rather than carrying a subview they never show.
    growthView,
    growthDive: mode === 'growth' ? growthDive : undefined,
    growthFacet: mode === 'growth' ? growthFacet : undefined,
    growthChannel:
      mode === 'growth' && analyticsOpen
        ? params.get('gc') || null
        : mode === 'growth'
          ? null
          : undefined,
    growthModel:
      mode === 'growth' ? params.get('gm') || (demoLink ? TRACIFY_DEMO_LINK : null) : undefined,
    ...(mode === 'growth' && groupDemo ? { growthDemo: true } : {}),
    horizonDate: mode === 'horizon' ? params.get('hd') || null : undefined,
    horizonOutlet: mode === 'horizon' ? params.get('ho') || null : undefined,
    horizonWindow: mode === 'horizon' ? params.get('hw') || null : undefined,
    hotelView: mode === 'hotel' ? parseHotelTab(params.get('hv')) : undefined,
    assistantPane:
      mode === 'assistant'
        ? params.get('ap') === 'dash'
          ? 'dashboard'
          : 'notebook'
        : undefined,
    ...(params.has('vc') ? { viewContextMarked: true } : {}),
  };
}

export function encodeOpsUrlState(state: OpsUrlState): URLSearchParams {
  const params = new URLSearchParams();
  const isComparing = state.compareFilters.length >= 2;

  const modeEnc = encodeMode(state.mode);
  if (modeEnc) params.set('m', modeEnc);

  if (isComparing) {
    for (const filter of state.compareFilters.slice(0, MAX_COMPARE_ITEMS)) {
      params.append('c', filter);
    }
  } else if (state.regionFilter && state.regionFilter !== 'all') {
    params.set('s', state.regionFilter);
    // A group or "All venues of" names its venues too: what a link shows.
    if (
      (state.regionFilter.startsWith('group:') || state.regionFilter === 'org') &&
      state.scopeVenueIds?.length
    ) {
      params.set('sv', [...new Set(state.scopeVenueIds)].sort().join(','));
    }
  }

  for (const id of state.source) {
    params.append('src', id);
  }
  for (const id of state.status) {
    params.append('st', id);
  }
  for (const name of state.shifts ?? []) {
    params.append('sh', name);
  }

  if (
    state.mode === 'growth' &&
    state.growthView &&
    state.growthView !== DEFAULT_GROWTH_SUBVIEW
  ) {
    params.set('gv', state.growthView);
  }
  if (state.mode === 'growth' && state.growthDive) {
    params.set('gd', state.growthDive);
  }
  if (state.mode === 'growth' && state.growthDive && state.growthFacet) {
    params.set('gf', state.growthFacet);
  }
  if (
    state.mode === 'growth' &&
    state.growthView === 'website' &&
    state.growthDive === 'site' &&
    state.growthChannel
  ) {
    params.set('gc', state.growthChannel);
  }
  if (state.mode === 'growth' && state.growthModel) {
    params.set('gm', state.growthModel);
  }
  if (state.mode === 'growth' && state.growthDemo) {
    params.set('demo', GROWTH_DEMO_LINK);
  }
  if (state.mode === 'horizon' && state.horizonDate) {
    params.set('hd', state.horizonDate);
  }
  if (state.mode === 'horizon' && state.horizonOutlet) {
    params.set('ho', state.horizonOutlet);
  }
  if (state.mode === 'horizon' && state.horizonWindow) {
    params.set('hw', state.horizonWindow);
  }
  if (state.mode === 'hotel' && state.hotelView === 'management') {
    params.set('hv', 'management');
  }
  if (state.mode === 'assistant' && state.assistantPane === 'dashboard') {
    params.set('ap', 'dash');
  }

  const encodePeriod =
    state.mode === 'performance' ||
    state.mode === 'growth' ||
    (state.mode === 'assistant' && state.assistantPane === 'dashboard');

  if (encodePeriod) {
    if (state.perfSettings.aggregation !== DEFAULT_PERF_SETTINGS.aggregation) {
      params.set('agg', state.perfSettings.aggregation);
    }
    if (state.perfSettings.frameCount !== DEFAULT_PERF_SETTINGS.frameCount) {
      params.set('fc', String(state.perfSettings.frameCount));
    }
    if (state.perfSettings.frameUnit !== DEFAULT_PERF_SETTINGS.frameUnit) {
      params.set('fu', state.perfSettings.frameUnit);
    }
    if (state.perfSettings.dateStart) {
      params.set('ds', state.perfSettings.dateStart);
    }
    if (state.perfSettings.dateEnd) {
      params.set('de', state.perfSettings.dateEnd);
    }
    if (
      (state.perfSettings.capacityBasis ?? DEFAULT_CAPACITY_BASIS) !==
      DEFAULT_CAPACITY_BASIS
    ) {
      params.set('cb', state.perfSettings.capacityBasis as string);
    }
    if (state.perfDrill?.metricKey) {
      const pd = state.perfDrill.outletId
        ? `${encodeMetricKey(state.perfDrill.metricKey)}~${state.perfDrill.outletId}`
        : encodeMetricKey(state.perfDrill.metricKey);
      params.set('pd', pd);
    }
    // Only an explicit choice goes into the URL, so Growth can tell it from
    // a default (WP3.1); "previous period" chosen on Performance included.
    if (state.perfSettings.compareExplicit) {
      params.set('cm', effectiveCompareKind(state.perfSettings));
    }
    if (state.perfSettings.compareDateStart) {
      params.set('cds', state.perfSettings.compareDateStart);
    }
    if (state.perfSettings.compareDateEnd) {
      params.set('cde', state.perfSettings.compareDateEnd);
    }
  } else if (perfSettingsDiffer(state.perfSettings, DEFAULT_PERF_SETTINGS)) {
    // Preserve perf settings in URL even in live mode so shared links restore them
    // when the recipient switches to Performance.
    params.set('agg', state.perfSettings.aggregation);
    params.set('fc', String(state.perfSettings.frameCount));
    params.set('fu', state.perfSettings.frameUnit);
    if (state.perfSettings.dateStart) {
      params.set('ds', state.perfSettings.dateStart);
    }
    if (state.perfSettings.dateEnd) {
      params.set('de', state.perfSettings.dateEnd);
    }
    if (
      (state.perfSettings.capacityBasis ?? DEFAULT_CAPACITY_BASIS) !==
      DEFAULT_CAPACITY_BASIS
    ) {
      params.set('cb', state.perfSettings.capacityBasis as string);
    }
    // Only an explicit choice goes into the URL, so Growth can tell it from
    // a default (WP3.1); "previous period" chosen on Performance included.
    if (state.perfSettings.compareExplicit) {
      params.set('cm', effectiveCompareKind(state.perfSettings));
    }
    if (state.perfSettings.compareDateStart) {
      params.set('cds', state.perfSettings.compareDateStart);
    }
    if (state.perfSettings.compareDateEnd) {
      params.set('cde', state.perfSettings.compareDateEnd);
    }
  }

  if (state.mode === 'live' && state.drillKey && !isComparing) {
    params.set('drill', state.drillKey);
  }

  if (state.focusOutletId && !isComparing) {
    params.set('focus', state.focusOutletId);
  }

  if (state.northStarDrill && !isComparing) {
    params.set('ns', '1');
  }

  if (state.viewContextMarked) {
    params.set('vc', '1');
  }

  return params;
}

export function opsStateFromView(args: {
  mode: OpsUrlMode;
  regionFilter: string | null;
  scopeVenueIds?: string[];
  compareItems: ComparisonItem[];
  source: ReservationSourceId[];
  status: ReservationStatusGroup[];
  shifts?: string[];
  perfSettings: PerfSettings;
  drillKey: string | null;
  perfDrill: { metricKey: string; outletId: string | null } | null;
  focusOutletId: string | null;
  northStarDrill: boolean;
  growthView?: GrowthSubview;
  growthDive?: GrowthDive | null;
  growthFacet?: GrowthFacet | null;
  growthChannel?: string | null;
  growthModel?: string | null;
  growthDemo?: boolean;
  horizonDate?: string | null;
  horizonOutlet?: string | null;
  horizonWindow?: string | null;
  hotelView?: HotelTab;
  assistantPane?: 'notebook' | 'dashboard';
  viewContextMarked?: boolean;
}): OpsUrlState {
  const isComparing = args.compareItems.length >= 2;
  return {
    mode: args.mode,
    regionFilter: args.regionFilter ?? 'all',
    ...(args.scopeVenueIds?.length ? { scopeVenueIds: args.scopeVenueIds } : {}),
    compareFilters: isComparing
      ? args.compareItems.map(i => i.filter)
      : [],
    source: args.source,
    status: args.status,
    shifts: args.shifts ?? [],
    perfSettings: args.perfSettings,
    drillKey: args.drillKey,
    perfDrill: args.perfDrill,
    focusOutletId: args.focusOutletId,
    northStarDrill: args.northStarDrill,
    growthView:
      args.mode === 'growth' ? (args.growthView ?? DEFAULT_GROWTH_SUBVIEW) : undefined,
    growthDive: args.mode === 'growth' ? (args.growthDive ?? null) : undefined,
    growthFacet: args.mode === 'growth' ? (args.growthFacet ?? null) : undefined,
    growthChannel: args.mode === 'growth' ? (args.growthChannel ?? null) : undefined,
    growthModel: args.mode === 'growth' ? (args.growthModel ?? null) : undefined,
    ...(args.mode === 'growth' && args.growthDemo ? { growthDemo: true } : {}),
    horizonDate: args.mode === 'horizon' ? (args.horizonDate ?? null) : undefined,
    horizonOutlet: args.mode === 'horizon' ? (args.horizonOutlet ?? null) : undefined,
    horizonWindow: args.mode === 'horizon' ? (args.horizonWindow ?? null) : undefined,
    hotelView: args.mode === 'hotel' ? (args.hotelView ?? 'service') : undefined,
    assistantPane: args.mode === 'assistant' ? (args.assistantPane ?? 'notebook') : undefined,
    ...(args.viewContextMarked ? { viewContextMarked: true } : {}),
  };
}

export function compareItemsFromFilters(
  filters: string[],
  scopeGroups: ScopeGroupSummary[],
  fleetRestaurants?: FleetRestaurant[],
): ComparisonItem[] {
  return filters.slice(0, MAX_COMPARE_ITEMS).map((filter, idx) => {
    const groupId = scopeGroupIdFromFilter(filter);
    const group = groupId
      ? scopeGroups.find(g => g.id === groupId)
      : undefined;
    return {
      filter,
      label: scopeFilterLabel(filter, scopeGroups, fleetRestaurants),
      color: COMPARE_COLORS[idx % COMPARE_COLORS.length],
      dataSource: group?.dataSource,
    };
  });
}

export function buildShareUrl(restaurantId: string, state: OpsUrlState): string {
  const params = encodeOpsUrlState(state);
  const qs = params.toString();
  const path = `/r/${encodeURIComponent(restaurantId)}`;
  return qs ? `${path}?${qs}` : path;
}

export function replaceOpsUrl(restaurantId: string, state: OpsUrlState): void {
  if (typeof window === 'undefined') return;
  const url = buildShareUrl(restaurantId, state);
  const absolute = `${window.location.origin}${url}`;
  if (window.location.href === absolute) return;
  window.history.replaceState(null, '', url);
}

export type UrlBootstrap = {
  hasShareParams: boolean;
  state: OpsUrlState | null;
};

export function getUrlBootstrap(): UrlBootstrap {
  if (typeof window === 'undefined') {
    return { hasShareParams: false, state: null };
  }
  const search = window.location.search;
  if (!hasShareParams(search)) {
    return { hasShareParams: false, state: null };
  }
  return {
    hasShareParams: true,
    state: decodeOpsUrlState(search),
  };
}
